import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import last from 'lodash/last';

import { ItemDragLayer } from 'Shared/components/DragAndDropComponents';
import { WorkspaceItem, SelectedSort, ItemPosition } from 'Shared/constants/types';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import { saveActionItemOrder } from 'Shared/actions/Goals.actions';
import ActionItemsWithNestingTable from 'Shared/components/ActionItems/ActionItemsWithNestingTable/ActionItemsWithNestingTable';
import ReadOnlyActionItemsWithNestingTable from 'Shared/components/ActionItems/ReadOnlyActionItemsWithNestingTable/ReadOnlyActionItemsWithNestingTable';
import { createItemFromSourceItemAction } from 'Shared/actions/meetingItems';
import GoalDetailsPageAiSuggestedSubGoalsOrActionItems from '../GoalDetailsPageAiSuggestedSubGoalsOrActionItems/GoalDetailsPageAiSuggestedSubGoalsOrActionItems';

import './GoalDetailsPageActionItemsList.scss';

type Props = {
  goalSlug: string;
  selectedSort: SelectedSort;
  actionItems: Array<WorkspaceItem>;
  isComplete: boolean;
  onAddItemClick(): void;
  handleBulkAddItems(bodies: string[]): void;
  setSelectedSort: Function;
  goalIsReadOnly: boolean;
};

function GoalDetailsPageActionItemsList({
  goalSlug,
  actionItems,
  onAddItemClick,
  handleBulkAddItems,
  selectedSort,
  setSelectedSort,
  isComplete,
  goalIsReadOnly,
}: Props) {
  const dispatch = useDispatch();
  const lastItemUuid = last(actionItems)?.uuid;

  const handleActionItemDrop = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoverOffset }) => {
      dispatch(
        saveActionItemOrder({
          draggedItemUuids,
          hoveredItemUuid: hoveredItemUuid || lastItemUuid,
          hoverOffset,
          goalSlug,
        })
      );
    },
    [dispatch, goalSlug, lastItemUuid]
  );

  const handleCreateOnEnter = useCallback(
    ({ sourceItemUuid, itemUuid }) => {
      dispatch(
        createItemFromSourceItemAction({
          sourceItemUuid,
          itemUuid,
          positionField: ItemPosition.positionForGoalDetailPage,
        })
      );
      dispatch(
        saveActionItemOrder({
          draggedItemUuids: [itemUuid],
          hoveredItemUuid: sourceItemUuid,
          hoverOffset: 1,
          goalSlug,
        })
      );
    },
    [dispatch, goalSlug]
  );

  return (
    <div className="GoalDetailsPageActionItemsList">
      <ItemDragLayer />
      {!goalIsReadOnly && (
        <ActionItemsWithNestingTable
          withOwnerSection
          withWorkspaceSection={false}
          positionField={ItemPosition.positionForGoalDetailPage}
          actionItems={actionItems}
          selectedSort={selectedSort}
          onActionItemDrop={handleActionItemDrop}
          onCreateItem={handleCreateOnEnter}
          setSelectedSort={setSelectedSort}
        />
      )}
      {goalIsReadOnly && (
        <ReadOnlyActionItemsWithNestingTable
          withOwnerSection
          withWorkspaceSection={false}
          positionField={ItemPosition.positionForGoalDetailPage}
          actionItems={actionItems}
          selectedSort={selectedSort}
          onActionItemDrop={handleActionItemDrop}
          onCreateItem={handleCreateOnEnter}
          setSelectedSort={setSelectedSort}
        />
      )}
      {isComplete || goalIsReadOnly ? null : (
        <div className="GoalDetailsPageActionItemsList__add d-flex py-2 pl-3">
          <WpButton variant="tertiary" onClick={onAddItemClick}>
            <WpIcon name="plus-circle" />
            Add Action Item
          </WpButton>
          <GoalDetailsPageAiSuggestedSubGoalsOrActionItems
            goalSlug={goalSlug}
            handleBulkAddItems={handleBulkAddItems}
            suggestedObjectType={'action_item'}
          />
        </div>
      )}
    </div>
  );
}

export default GoalDetailsPageActionItemsList;
